import { defineStore } from "pinia";
import { onAuthStateChanged, User, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as logOut, getIdTokenResult, sendPasswordResetEmail } from "firebase/auth"
import type { ParsedToken } from 'firebase/auth'
import { auth } from "@/util/firebase";
import { getAuth } from "firebase/auth";
import { computed, ref } from "vue";
import { Userspaces } from '@busy-human/k9-library';
import { useUserspace } from '../store/useUsers'
import { FirebaseError } from "@firebase/util";
import { createNewUser } from "@/util/functions";
import { setRaygunUserEventGenerator, updateRaygunUser } from "@busy-human/raygun-util";


var chars = "0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ";


const AuthErrorMap: {[code: string]: string} = {
    'auth/invalid-email': "Invalid Email",
    'auth/user-not-found': "User Not Found",
    'auth/wrong-password': "Password Invalid",
    'auth/email-already-in-use': "Email Already In Use",
    'auth/user-not-admin': "User Is Not Admin"
}

function ConvertAuthError(errorCode: string) {
    // return AuthErrorMap[errorCode] || "Unknown"
    return AuthErrorMap[errorCode] || errorCode
}

// eslint-disable-next-line max-lines-per-function
export const useAuth = defineStore('auth', () => {
    const user = ref<User | null>(null);
    const _claims = ref<ParsedToken | null>(null);
    const _authready = ref<boolean>(false);

    onAuthStateChanged(auth, async userAuth => {
        user.value = userAuth;
        _authready.value = true;
        console.log("Auth State Changed", user.value !== null ? "Authenticated" : "Unauthenticated");

        if(userAuth) {
            const tokenResult = await getIdTokenResult(userAuth);
            _claims.value = tokenResult.claims;

            if(!_claims.value['isAdmin']) {
                console.log("User is not an admin, signing out")
                user.value = null;
                _claims.value = null;
                
                setTimeout(async()=>{
                    await signOut()
                },5000)
            } else {
                updateRaygunUser(userAuth);
            }
        } else {
            _claims.value = null;
        }

        authWaitingPromises.value.map(res => res());
        authWaitingPromises.value = [];
    })

    const isAuthReady = computed(() => _authready.value);
    const isAuthenticated = computed(() => user.value !== null);
    const claims = computed(() => _claims.value);

    async function signIn(email: string, password: string) {
        try {
            const creds = await signInWithEmailAndPassword(auth, email, password);
            user.value = creds.user;

            const tokenResult = await getIdTokenResult(creds.user);
            _claims.value = tokenResult.claims;

            if(!_claims.value['isAdmin']) {
                throw new FirebaseError('auth/user-not-admin', "User is not an admin");
            }
            
            return { success: true };
        } catch (e) {
            console.error(e);
            return { success: false, reason: ConvertAuthError((e as FirebaseError).code) }
        }
    }

    async function sendPasswordReset(email: string) {
        return sendPasswordResetEmail(auth, email)
    }

    async function createUserAndResetPass(newUser: Userspaces.Model) {
        try {
            await createNewUser({user: newUser});
            await  sendPasswordReset(newUser.email);
            return { success: true};
        } catch (e) {
            console.error(e);
            return { success: false, reason: ConvertAuthError((e as FirebaseError).code) }
        }
    }

    async function signOut() {
        try {
            await logOut(auth);
        } catch (e) {
            console.error(e);
        }
    }

    const authWaitingPromises = ref<(() => void)[]>([]);
    async function waitForAuth() {
        if(isAuthReady.value) return;
        else {
            return new Promise<void>(res => authWaitingPromises.value.push(res));
        }
    }

    setRaygunUserEventGenerator((userData) => {
        if(userData) {
            if(userData.isAnonymous) {
                return {
                    identifier: userData.uid,
                    email: '',
                    isAnonymous: true
                }
            } else {
                return {
                    identifier: userData.uid,
                    email: userData.email || '',
                    isAnonymous: false
                }
            }
        } else {
            return {
                identifier: '',
                email: '',
                isAnonymous: true
            }
        }
    })

    const currentUid = computed(() => user.value?.uid);

    return { user, isAuthReady, isAuthenticated, signIn, signOut, waitForAuth, createUserAndResetPass, sendPasswordReset, claims, currentUid }
})